import {Component, OnInit, signal, viewChild} from '@angular/core';
import {uniq} from 'lodash';
import {EvenementService} from '../shared/services/evenement.service';
import {AuthService} from '../shared/services/auth-service.service';
import {PrimeNGConfig, TreeNode} from 'primeng/api';
import {finalize, take} from 'rxjs/operators';
import {AlertService, CodeDesignation, ConfirmationAction, ConfirmationData, ConfirmationModalService, DateUtils, isNotNullOrUndefined} from 'sigale-angular';
import {TreeTableDataValidation} from './model/tree-table-data-validation.model';
import {TranslateService} from '@ngx-translate/core';
import {DateUtilsService} from '../shared/services/date-utils.service';
import {PremierDernierJoursSemaine} from '../shared/models/premier-dernier-jours-semaine.model';
import {PcalendarLanguageService} from '@sigale-angular/src/services/pcalendar-language.service';
import {UtilisateurSigalett} from '../shared/models/utilisateur-sigalett.model';
import {ValiderRefuserJoursTeletravailDto} from './model/valider-refuser-jours-teletravail.dto';
import {StatutEvenement} from '../shared/models/statut-evenement.model';
import {EvenementDto} from '../shared/dto/evenement.dto';
import {TreeTable} from 'primeng/treetable';
import {TypeEvenement} from '../shared/models/type-evenement.model';

@Component({
	selector: 'app-validation-teletravail',
	templateUrl: './validation-teletravail.component.html',
	styleUrl: './validation-teletravail.component.scss'
})
export class ValidationTeletravailComponent implements OnInit {

	readonly NOM_ALERTE_VALIDATION_TELETRAVAIL = 'alerteValidationTeletravail';

	evenementsValidation = viewChild<TreeTable>('evenementsValidation');

	loadingRechercherJoursTeletravail = signal<boolean>(false);
	evenementsParSemaine = signal<Map<string, EvenementDto[]>>(new Map<string, EvenementDto[]>());
	donneesTreeTableValidation = signal<TreeNode<TreeTableDataValidation>[]>([]);
	loadingBtnValiderParJourTeletravailId = signal<Map<number, boolean>>(new Map<number, boolean>());
	loadingBtnRefuserParJourTeletravailId = signal<Map<number, boolean>>(new Map<number, boolean>());
	loadingBtnValiderParSemaine = signal<Map<string, boolean>>(new Map<string, boolean>());
	loadingBtnRefuserParSemaine = signal<Map<string, boolean>>(new Map<string, boolean>());

	periode: Date[] | undefined;
	utilisateurConnecte: UtilisateurSigalett;
	tousLesStatutsTeletravail: { label: string, value: string }[] = [];
	tousLesTypesEvenements: { label: string, value: string }[] = [];
	filtreStatutsTeletravail = signal<string[]>([]);
	filtreTypesEvenementTeletravail = signal<string[]>([]);

	constructor(
		private jourTeletravailService: EvenementService,
		private authService: AuthService,
		private alertService: AlertService,
		private translateService: TranslateService,
		private dateUtilsService: DateUtilsService,
		private pcalendarLanguageService: PcalendarLanguageService,
		private confirmationModalService: ConfirmationModalService,
		private primengConfig: PrimeNGConfig) {
	}

	ngOnInit() {
		this.utilisateurConnecte = this.authService.getUtilisateurConnecte();

		const langueUtilisateur = this.utilisateurConnecte.langue;
		const labelsTraduis = this.pcalendarLanguageService.traduire(langueUtilisateur);
		this.primengConfig.setTranslation(labelsTraduis);

		this.periode = [];
		const premierEtDernierJoursSemaine = this.dateUtilsService.recupererPremierEtDernierJourSemaine(new Date());

		this.periode[0] = premierEtDernierJoursSemaine.premierJourSemaine;
		this.periode[1] = null;

		this.recupererEvenementsEquipierEtPeriode();
	}

	/**
	 * Récupère les évènements du responsable pour une période.
	 */
	recupererEvenementsEquipierEtPeriode(): void {
		this.loadingRechercherJoursTeletravail.set(true);

		const premierJourSemaineDateDebut = this.dateUtilsService.recupererPremierEtDernierJourSemaine(this.periode[0]).premierJourSemaine;

		const dateDebut = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(premierJourSemaineDateDebut, this.utilisateurConnecte.langue);

		let dateFin: string;
		if (isNotNullOrUndefined(this.periode[1])) {
			const dernierJourSemaineDateFin = this.dateUtilsService.recupererPremierEtDernierJourSemaine(this.periode[1]).dernierJoursSemaine;
			dateFin = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(dernierJourSemaineDateFin, this.utilisateurConnecte.langue);
		}

		this.jourTeletravailService.recupererEvenementsEquipierEtPeriode(this.utilisateurConnecte.login, dateDebut, dateFin)
			.pipe(finalize(() => this.loadingRechercherJoursTeletravail.set(false)))
			.subscribe({
				next: (joursTeletravail) => {
					const statutsTeletravail = joursTeletravail.map(jourTeletravail => jourTeletravail.statut);
					const designationsStatuts = statutsTeletravail.map(statut => statut.designation);
					this.tousLesStatutsTeletravail = uniq(designationsStatuts)
						.map(designation => ({
							label: designation,
							value: designation
						}))
						.sort((a, b) => a.label.localeCompare(b.label));

					const typesEvenement = joursTeletravail.map(jourTeletravail => jourTeletravail.typeEvenement);
					const designationsTypes = typesEvenement.map(type => type.designation);
					this.tousLesTypesEvenements = uniq(designationsTypes)
						.map(designation => ({
							label: designation,
							value: designation
						})).sort((a, b) => a.label.localeCompare(b.label));

					this.evenementsParSemaine.set(this.grouperEvenementsParSemaine(joursTeletravail));
					this.convertirMapEnTreeDate();

					const statutEnAttente = statutsTeletravail.filter(statut => statut.code === StatutEvenement.EN_ATTENTE);

					if (statutEnAttente.length > 0) {
						this.filtreStatutsTeletravail.set([statutEnAttente[0].designation]);
						this.evenementsValidation().filter(this.filtreStatutsTeletravail(), 'statut.designation', 'in');
					}
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
					this.alertService.error(error.error, this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
				}
			});
	}

	/**
	 * Converti la map d'évènements par semaine en données pour la treeTable.
	 */
	convertirMapEnTreeDate() {
		const treeNodeData: TreeNode<TreeTableDataValidation>[] = [];
		const loadingParSemaine = new Map<string, boolean>();
		const loadingParJourTeletravail = new Map<number, boolean>();

		this.evenementsParSemaine().forEach((joursTeletravail: EvenementDto[], labelSemaine: string) => {
			// Noeud parent
			const parentNode: TreeNode<TreeTableDataValidation> = {
				data: {
					labelSemaine: labelSemaine,
					type: 'parent'
				},
				children: [],
				expanded: true
			};

			loadingParSemaine.set(labelSemaine, false);

			parentNode.children = joursTeletravail.map((evenement) => ({
				data: {
					id: evenement.id,
					labelSemaine: labelSemaine,
					nomUtilisateur: evenement.demandeur.nom,
					prenomUtilisateur: evenement.demandeur.prenom,
					typeEvenement: evenement.typeEvenement,
					dateTeletravail: this.dateUtilsService.formaterDateEnFormatLettre(new Date(evenement.dateEvenement), this.utilisateurConnecte.langue),
					statut: evenement.typeEvenement.code === TypeEvenement.TELETRAVAIL ? evenement.statut : new CodeDesignation('N/A', 'N/A'),
					duree: evenement.duree,
					dateDemande: this.dateUtilsService.formaterDateEnFormatLettre(new Date(evenement.dateDemande), this.utilisateurConnecte.langue),
					commentaire: evenement.commentaire,
					type: 'enfant'
				}
			}));

			joursTeletravail.forEach(jourTeletravail => loadingParJourTeletravail.set(jourTeletravail.id, false));

			treeNodeData.push(parentNode);
		});

		this.loadingBtnValiderParSemaine.set(loadingParSemaine);
		this.loadingBtnRefuserParSemaine.set(loadingParSemaine);
		this.loadingBtnValiderParJourTeletravailId.set(loadingParJourTeletravail);
		this.loadingBtnRefuserParJourTeletravailId.set(loadingParJourTeletravail);

		this.donneesTreeTableValidation.set(treeNodeData);
	}

	/**
	 * Récupère le label des semaines (Semaine du x au x mois année).
	 * @param premierDernierJoursSemaine le prmeier et dernie jours de la semaine.
	 */
	recupererLabelSemaine(premierDernierJoursSemaine: PremierDernierJoursSemaine): string {
		const premierJour = premierDernierJoursSemaine.premierJourSemaine;
		const dernierJour = premierDernierJoursSemaine.dernierJoursSemaine;

		const premierePartieMessage = this.translateService.instant('VALIDATION_TELETRAVAIL.TABLE.LABEL_SEMAINE_PREMIERE_PARTIE');
		const deuxiemePartieMessage = this.translateService.instant('VALIDATION_TELETRAVAIL.TABLE.LABEL_SEMAINE_DEUXIEME_PARTIE');

		const datePremierJour = premierJour.getDate();
		const dateDernierJour = dernierJour.getDate();
		const moisPremierJour = this.dateUtilsService.recupererMoisTraduis(premierJour.getMonth());
		const moisDernierJour = this.dateUtilsService.recupererMoisTraduis(dernierJour.getMonth());
		const anneePremierJour = premierJour.getFullYear();
		const anneeDernierJour = dernierJour.getFullYear();

		if (anneePremierJour !== anneeDernierJour) {
			return `${premierePartieMessage} ${datePremierJour} ${moisPremierJour} ${anneePremierJour} ${deuxiemePartieMessage} ${dateDernierJour} ${moisDernierJour} ${anneeDernierJour}`;
		}

		if (moisPremierJour !== moisDernierJour) {
			return `${premierePartieMessage} ${datePremierJour} ${moisPremierJour} ${deuxiemePartieMessage} ${dateDernierJour} ${moisDernierJour} ${anneeDernierJour}`;
		}

		return `${premierePartieMessage} ${datePremierJour} ${deuxiemePartieMessage} ${dateDernierJour} ${moisDernierJour} ${anneeDernierJour}`;
	}

	/**
	 * Groupe les évènements par semaine.
	 * @param evenements les évènements à grouper.
	 */
	grouperEvenementsParSemaine(evenements: EvenementDto[]): Map<string, EvenementDto[]> {
		const evenementsParSemaine: Map<string, EvenementDto[]> = new Map<string, EvenementDto[]>();

		evenements.forEach((jourTeletravail) => {
			const premierDernierJoursSemaine = this.dateUtilsService.recupererPremierEtDernierJourSemaine(new Date(jourTeletravail.dateEvenement));
			const labelSemaine = this.recupererLabelSemaine(premierDernierJoursSemaine);

			if (evenementsParSemaine.has(labelSemaine)) {
				evenementsParSemaine.get(labelSemaine).push(jourTeletravail);
			}
			else {
				evenementsParSemaine.set(labelSemaine, [jourTeletravail]);
			}
		});

		return evenementsParSemaine;
	}

	/**
	 * Détermine la classe de la div en fonction du statut pour afficher une couleur différente.
	 * @param statut le statut.
	 */
	determinerClasseTagStatut(statut: CodeDesignation): string {
		return `statut-${statut.code}`;
	}

	/**
	 * Action effectuer lors de la sélection d'une période dans le calendrier.
	 */
	onSelectPeriode() {
		if (this.periode && this.periode.length === 2 && this.periode[0] && this.periode[1]) {
			// Les deux dates sont choisies, déclenchez votre appel back ici
			this.recupererEvenementsEquipierEtPeriode();
		}
	}

	/**
	 * Ne conserve que les évènements de type télétravail, non validés/refusés, non passés et éventuellement filtrés dans la table.
	 * @param statutAEcarter le statut à écarter (validé ou refusé).
	 */
	private joursEvenementsAConserverPourValidationOuRefus(statutAEcarter: StatutEvenement) {
		return (jourTeletravail: EvenementDto) => jourTeletravail.statut.code !== statutAEcarter
			&& jourTeletravail.typeEvenement.code === TypeEvenement.TELETRAVAIL
			&& !DateUtils.isDayBefore(jourTeletravail.dateEvenement, new Date())
			&& (!this.evenementsValidation().filteredNodes ||
				this.evenementsValidation().filteredNodes.find(nodeFiltered => nodeFiltered.children.find(child => child.data.id === jourTeletravail.id))
			);
	}

	/**
	 * Valide les jours de télétravail de la semaine passée en paramètre.
	 * @param labelSemaineKey la clé de la semaine passée en paramètre.
	 */
	validerJoursTeletravailPourSemaine(labelSemaineKey: string): void {
		this.loadingBtnValiderParSemaine.update(loadingParSemaine => {
			return new Map(loadingParSemaine).set(labelSemaineKey, true);
		});

		const joursTeletravailIds = this.evenementsParSemaine().get(labelSemaineKey)
			.filter(this.joursEvenementsAConserverPourValidationOuRefus(StatutEvenement.VALIDE))
			.map((jourTeletravail) => jourTeletravail.id);

		const validerRefuserDto: ValiderRefuserJoursTeletravailDto = new ValiderRefuserJoursTeletravailDto(joursTeletravailIds, StatutEvenement.VALIDE, this.utilisateurConnecte.login);

		this.jourTeletravailService.validerRefuserJoursTeletravail(validerRefuserDto)
			.pipe(finalize(() => {
				this.loadingBtnValiderParSemaine.update(loadingParSemaine => {
					return new Map(loadingParSemaine).set(labelSemaineKey, false);
				});
			}))
			.subscribe({
				next: (nouveauStatut) => {
					this.mettreAJourDonneesSuiteValidationRefus(joursTeletravailIds, nouveauStatut);
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
					this.alertService.error(error.error, this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
				}
			});
	}

	/**
	 * Refuse les jours de télétravail pour la semaine.
	 * @param labelSemaineKey la clé de la semaine passée en paramètre.
	 */
	refuserJoursTeletravailPourSemaine(labelSemaineKey: string): void {
		this.loadingBtnRefuserParSemaine.update(loadingParSemaine => {
			return new Map(loadingParSemaine).set(labelSemaineKey, true);
		});

		const joursTeletravailIds = this.evenementsParSemaine().get(labelSemaineKey)
			.filter(this.joursEvenementsAConserverPourValidationOuRefus(StatutEvenement.REFUSE))
			.map((jourTeletravail) => jourTeletravail.id);

		const validerRefuserDto: ValiderRefuserJoursTeletravailDto = new ValiderRefuserJoursTeletravailDto(joursTeletravailIds, StatutEvenement.REFUSE, this.utilisateurConnecte.login);

		this.jourTeletravailService.validerRefuserJoursTeletravail(validerRefuserDto)
			.pipe(finalize(() => {
				this.loadingBtnRefuserParSemaine.update(loadingParSemaine => {
					return new Map(loadingParSemaine).set(labelSemaineKey, false);
				});
			}))
			.subscribe({
				next: (nouveauStatut) => {
					this.mettreAJourDonneesSuiteValidationRefus(joursTeletravailIds, nouveauStatut);
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
					this.alertService.error(error.error, this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
				}
			});
	}

	/**
	 * Met à jour les données (jours télétravail et données de la table) après la validation ou le refus des jours de télétravail.
	 * @param ids les ids des jours de télétravail à mettre à jour.
	 * @param nouveauStatut le nouveau statut.
	 * @private
	 */
	private mettreAJourDonneesSuiteValidationRefus(ids: number[], nouveauStatut: CodeDesignation) {
		this.donneesTreeTableValidation.update(treeNodeTableValidation => {
			return treeNodeTableValidation.map(treeNode => {
					const childrenMisAJour = treeNode.children.map(child => {
						return ids.includes(child.data.id) ? {...child, data: {...child.data, statut: nouveauStatut}} : child;
					});
					return {...treeNode, children: childrenMisAJour};
				}
			);
		});
		this.evenementsParSemaine.update(joursTeletravailParSemaine => {
			const nouvelleMap = new Map(joursTeletravailParSemaine);
			joursTeletravailParSemaine.forEach((joursTeletravail, semaine) => {
				const nouveauTableau = joursTeletravail.map(jourTeletravail => {
					return ids.includes(jourTeletravail.id) ? {...jourTeletravail, statut: nouveauStatut} : jourTeletravail;
				});
				nouvelleMap.set(semaine, nouveauTableau);
			});
			return nouvelleMap;
		});
	}

	/**
	 * Valide un jour de télétravail.
	 * @param id l'id du jour de télétravail à valider.
	 */
	validerJoursTeletravailUnitaire(id: number): void {
		this.loadingBtnValiderParJourTeletravailId.update(loadingParId => {
			return new Map(loadingParId).set(id, true);
		});

		const validerRefuserDto: ValiderRefuserJoursTeletravailDto = new ValiderRefuserJoursTeletravailDto([id], StatutEvenement.VALIDE, this.utilisateurConnecte.login);

		this.jourTeletravailService.validerRefuserJoursTeletravail(validerRefuserDto)
			.pipe(finalize(() => {
				this.loadingBtnValiderParJourTeletravailId.update(loadingParId => {
					return new Map(loadingParId).set(id, false);
				});
			}))
			.subscribe({
				next: (nouveauStatut) => {
					this.mettreAJourDonneesSuiteValidationRefus([id], nouveauStatut);
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
					this.alertService.error(error.error, this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
				}
			});
	}

	/**
	 * Refuse un jour de télétravail.
	 * @param id l'id du jour de télétravail à refuser.
	 */
	refuserJoursTeletravailUnitaire(id: number): void {
		this.loadingBtnRefuserParJourTeletravailId.update(loadingParId => {
			return new Map(loadingParId).set(id, true);
		});

		const validerRefuserDto: ValiderRefuserJoursTeletravailDto = new ValiderRefuserJoursTeletravailDto([id], StatutEvenement.REFUSE, this.utilisateurConnecte.login);

		this.jourTeletravailService.validerRefuserJoursTeletravail(validerRefuserDto)
			.pipe(finalize(() => {
				this.loadingBtnRefuserParJourTeletravailId.update(loadingParId => {
					return new Map(loadingParId).set(id, false);
				});
			}))
			.subscribe({
				next: (nouveauStatut) => {
					this.mettreAJourDonneesSuiteValidationRefus([id], nouveauStatut);
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
					this.alertService.error(error.error, this.NOM_ALERTE_VALIDATION_TELETRAVAIL);
				}
			});
	}

	/**
	 * Disable ou non le bouton de validation de la semaine.
	 * @param labelSemaine le label de la semaine.
	 */
	estBtnSemaineValiderDisabled(labelSemaine: string): boolean {
		return this.estSemaineAvecDatesPassees(labelSemaine)
			|| this.loadingBtnRefuserParSemaine().get(labelSemaine)
			|| this.joursSemaineAvecMemeStatut(labelSemaine, StatutEvenement.VALIDE);
	}

	/**
	 * Disable ou non le bouton de refus de la semaine.
	 * @param labelSemaine le label de la semaine.
	 */
	estBtnSemaineRefuserDisabled(labelSemaine: string): boolean {
		return this.estSemaineAvecDatesPassees(labelSemaine)
			|| this.loadingBtnValiderParSemaine().get(labelSemaine)
			|| this.joursSemaineAvecMemeStatut(labelSemaine, StatutEvenement.REFUSE);
	}

	/**
	 * Disable ou non le bouton de validation du jour de télétravail.
	 * @param labelSemaine le label de la semaine.
	 * @param id l'id du jour de télétravail
	 * @param statut le statut du jour de télétravail.
	 * @param typeEvenement le type d'évènement du jour de télétravail.
	 */
	estBtnUnitaireValiderDisabled(labelSemaine: string, id: number, statut: CodeDesignation, typeEvenement: CodeDesignation): boolean {
		return typeEvenement.code !== TypeEvenement.TELETRAVAIL
			|| this.estDateTeletravailPassee(labelSemaine, id)
			|| this.estSemaineValidationLoading(labelSemaine)
			|| this.loadingBtnRefuserParJourTeletravailId().get(id)
			|| statut.code === StatutEvenement.VALIDE;
	}

	/**
	 * Disable ou non le bouton de refus du jour de télétravail.
	 * @param labelSemaine le label de la semaine.
	 * @param id l'id du jour de télétravail
	 * @param statut le statut du jour de télétravail
	 * @param typeEvenement le type d'évènement du jour de télétravail.
	 */
	estBtnUnitaireRefuserDisabled(labelSemaine: string, id: number, statut: CodeDesignation, typeEvenement: CodeDesignation): boolean {
		return typeEvenement.code !== TypeEvenement.TELETRAVAIL
			|| this.estDateTeletravailPassee(labelSemaine, id)
			|| this.estSemaineValidationLoading(labelSemaine)
			|| this.loadingBtnValiderParJourTeletravailId().get(id)
			|| statut.code === StatutEvenement.REFUSE;
	}

	/**
	 * Vrai si la semaine est en cours de validation.
	 * @param labelSemaine le label de la semaine.
	 * @private
	 */
	private estSemaineValidationLoading(labelSemaine: string): boolean {
		return this.loadingBtnValiderParSemaine().get(labelSemaine) || this.loadingBtnRefuserParSemaine().get(labelSemaine);
	}

	/**
	 * Vrai si la date de télétravail est dépassée (inférieure à aujourd'hui). Faux sinon
	 * @param labelSemaine le label de la semaine.
	 * @param id l'id du jour de télétravail.
	 * @private
	 */
	private estDateTeletravailPassee(labelSemaine: string, id: number): boolean {
		const jourTeletravail = this.evenementsParSemaine().get(labelSemaine).find(jourTeletravail => jourTeletravail.id === id);
		return DateUtils.isDayBefore(jourTeletravail.dateEvenement, new Date());
	}

	/**
	 * Vérifie si les jours de la semaine ont le même statut
	 * @param labelSemaine le label de la semaine.
	 * @param statut le statut.
	 * @private
	 */
	private joursSemaineAvecMemeStatut(labelSemaine: string, statut: StatutEvenement): boolean {
		const joursTeletravail = this.evenementsParSemaine().get(labelSemaine);
		const nombreJourStatutRefuser = joursTeletravail.filter(jourTeletravail => jourTeletravail.statut.code === statut).length;
		return nombreJourStatutRefuser === joursTeletravail.length;
	}

	/**
	 * Vrai si les dates de télétravail de la semaine sont dépassées (inférieure à aujourd'hui). Faux sinon
	 * @param labelSemaine le label de la semaine.
	 * @private
	 */
	private estSemaineAvecDatesPassees(labelSemaine: string): boolean {
		const joursTeletravail = this.evenementsParSemaine().get(labelSemaine);
		const nombreJourAvecDatePassee = joursTeletravail.filter(jourTeletravail => DateUtils.isDayBefore(jourTeletravail.dateEvenement, new Date())).length;
		return nombreJourAvecDatePassee === joursTeletravail.length;
	}

	/**
	 * Applique une classe aux jours de télétravail avec une date de télétravail inférieure à aujourd'hui.
	 * @param labelSemaine le label de la semaine.
	 * @param id l'id du jour de télétravail.
	 */
	determinerClasseDateDepassee(labelSemaine: string, id: number): string {
		const joursTeletravail = this.evenementsParSemaine().get(labelSemaine);
		const jourTeletravail = joursTeletravail.find(jourTeletravail => jourTeletravail.id === id);
		return DateUtils.isDayBefore(jourTeletravail.dateEvenement, new Date()) ? 'date-teletravail-depassee' : '';
	}


	/**
	 * Ouvre la popup de confirmation pour valider les jours de télétravail par semaine.
	 */
	validerJoursDeTeleTravailPourSemaineConfirmation(labelSemaineKey: string): void {
		const joursTeletravailAValider = this.evenementsParSemaine().get(labelSemaineKey)
			.filter(this.joursEvenementsAConserverPourValidationOuRefus(StatutEvenement.VALIDE));

		if (joursTeletravailAValider.length === 0) {
			return;
		}

		const boutonOui = new ConfirmationAction('GLOBAL.OUI');
		const boutonNon = new ConfirmationAction('GLOBAL.NON', 'link');

		boutonOui.onClick.pipe(take(1)).subscribe(() => {
			this.validerJoursTeletravailPourSemaine(labelSemaineKey);
			this.confirmationModalService.fermer();
		});

		boutonNon.onClick.pipe(take(1)).subscribe(() => {
			this.confirmationModalService.fermer();
		});

		const confirmationData = new ConfirmationData(
			'VALIDATION_TELETRAVAIL.POPUP_VALIDATION.TITRE',
			'VALIDATION_TELETRAVAIL.POPUP_VALIDATION.MESSAGE',
			[boutonOui, boutonNon],
			{nombre: joursTeletravailAValider.length}
		);
		this.confirmationModalService.ouvrir(confirmationData);
	}

	/**
	 * Ouvre la popup de confirmation pour refuser les jours de télétravail par semaine.
	 */
	refuserJoursDeTeleTravailPourSemaineConfirmation(labelSemaineKey: string): void {
		const joursTeletravailARefuser = this.evenementsParSemaine().get(labelSemaineKey)
			.filter(this.joursEvenementsAConserverPourValidationOuRefus(StatutEvenement.REFUSE));

		if (joursTeletravailARefuser.length === 0) {
			return;
		}

		const boutonOui = new ConfirmationAction('GLOBAL.OUI');
		const boutonNon = new ConfirmationAction('GLOBAL.NON', 'link');

		boutonOui.onClick.pipe(take(1)).subscribe(() => {
			this.refuserJoursTeletravailPourSemaine(labelSemaineKey);
			this.confirmationModalService.fermer();
		});

		boutonNon.onClick.pipe(take(1)).subscribe(() => {
			this.confirmationModalService.fermer();
		});

		const confirmationData = new ConfirmationData(
			'VALIDATION_TELETRAVAIL.POPUP_REFUS.TITRE',
			'VALIDATION_TELETRAVAIL.POPUP_REFUS.MESSAGE',
			[boutonOui, boutonNon],
			{nombre: joursTeletravailARefuser.length}
		);
		this.confirmationModalService.ouvrir(confirmationData);
	}

	onFiltreDateModifiee($event, evenementsValidation, nomDuChampAFiltrer) {
		if ($event.target.value) {
			const dateFormatee = this.dateUtilsService.formaterDateEnFormatLettre(new Date($event.target.value), this.utilisateurConnecte.langue);
			evenementsValidation.filter(dateFormatee, nomDuChampAFiltrer, 'contains');
		}
		else {
			evenementsValidation.filter("", nomDuChampAFiltrer, 'contains');
		}
	}

	onResetSemaineConcernee() {
		const premierEtDernierJoursSemaine = this.dateUtilsService.recupererPremierEtDernierJourSemaine(new Date());
		this.periode = [];
		this.periode[0] = premierEtDernierJoursSemaine.premierJourSemaine;
		this.periode[1] = null;
		this.recupererEvenementsEquipierEtPeriode();
	}
}
